import { makeAutoObservable, runInAction } from 'mobx';
import type { Study, StudyGroup, StudentAddOptions, StudentAssignment, WorkingStudy, BibleLocation } from '@skjv/common';
import {
  bulkSaveStudies,
  bulkSaveStudyGroups,
  hydrateDeferredLibrary,
  loadStudies,
  loadStudyGroups,
  saveStudies,
  saveStudyGroups,
} from '../persistence';
import { type ImportResult, type StudyStoreDeps, importFromJson, importRawStudyWithGroup } from './study-import';
import { migrateGroupLevels, migrateStudyLevels } from './level-migration';
import { exportStudyToJson, exportAllToJson } from './study-export';
import { type GroupStoreDeps, addStudyGroup, updateStudyGroup, getStudyGroup, setStudyGroupMemoryStart, setStudyGroupMemoryEnd } from './study-group-ops';
import { addStudentToGroup, updateStudent, removeStudentFromGroup, addTeacherToGroup, removeTeacherFromGroup, createAssignmentsForGroup } from './study-member-ops';
import { startNewStudy, saveEditingStudy } from './study-editing';

export type { ImportResult };

/**
 * Study state management.
 */
export class StudyState {
  studies: Study[] = [];
  studyGroups: StudyGroup[] = [];
  selectedStudyId: string | null = null;
  editingStudy: WorkingStudy | null = null;
  /** True while the desktop shell's deferred library snapshot is in flight —
   *  the study panel shows a loading status; sync-mirror studies (last
   *  session's copy) stay visible meanwhile. */
  isLibraryLoading = false;

  constructor() {
    makeAutoObservable(this);
    if (typeof window !== 'undefined') {
      // Loads are raw JSON casts — map legacy level enums in memory
      // (converges to disk on the next natural save; see level-migration.ts)
      this.studies = migrateStudyLevels(loadStudies<Study>());
      this.studyGroups = migrateGroupLevels(loadStudyGroups<StudyGroup>());
      this.hydrateLibrary();
    }
  }

  /** Desktop: the boot snapshot defers the study library so scripture paints
   *  first — pull it now and reload state from the reconciled mirror. */
  private hydrateLibrary(): void {
    this.isLibraryLoading = true;
    hydrateDeferredLibrary()
      .then((hydrated) => {
        runInAction(() => {
          if (hydrated) {
            this.studies = migrateStudyLevels(loadStudies<Study>());
            this.studyGroups = migrateGroupLevels(loadStudyGroups<StudyGroup>());
          }
          this.isLibraryLoading = false;
        });
      })
      .catch(() => {
        runInAction(() => {
          this.isLibraryLoading = false;
        });
      });
  }

  get selectedStudy(): Study | undefined {
    return this.studies.find((s) => s.id === this.selectedStudyId);
  }

  get sortedStudies(): Study[] {
    return [...this.studies].sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    );
  }

  get sortedStudyGroups(): StudyGroup[] {
    return [...this.studyGroups].sort((a, b) => {
      if (a.lastUsedAt && b.lastUsedAt) return new Date(b.lastUsedAt).getTime() - new Date(a.lastUsedAt).getTime();
      if (a.lastUsedAt) return -1;
      if (b.lastUsedAt) return 1;
      return a.name.localeCompare(b.name);
    });
  }

  private generateId(): string { return crypto.randomUUID(); }
  private now(): string { return new Date().toISOString(); }

  // ── Study CRUD ──────────────────────────────────────────────

  selectStudy(id: string | null): void { this.selectedStudyId = id; }

  addStudy(study: Omit<Study, 'id' | 'createdAt' | 'updatedAt'>): string {
    const id = this.generateId();
    const now = this.now();
    this.studies = [...this.studies, { ...study, id, createdAt: now, updatedAt: now }];
    this.saveStudies();
    return id;
  }

  updateStudy(id: string, updates: Partial<Omit<Study, 'id' | 'createdAt'>>): void {
    this.studies = this.studies.map((s) => (s.id === id ? { ...s, ...updates, updatedAt: this.now() } : s));
    this.saveStudies();
  }

  removeStudy(id: string): void {
    this.studies = this.studies.filter((s) => s.id !== id);
    if (this.selectedStudyId === id) this.selectedStudyId = null;
    this.saveStudies();
  }

  duplicateStudy(id: string): string | null {
    const study = this.studies.find((s) => s.id === id);
    if (!study) return null;
    const newId = this.generateId();
    const now = this.now();
    this.studies = [...this.studies, { ...study, id: newId, name: `${study.name} (copy)`, createdAt: now, updatedAt: now }];
    this.saveStudies();
    return newId;
  }

  // ── Study Editing (delegated) ───────────────────────────────

  private get editingDeps() {
    return {
      studies: this.studies, studyGroups: this.studyGroups, editingStudy: this.editingStudy,
      sortedStudies: this.sortedStudies, sortedStudyGroups: this.sortedStudyGroups,
      generateId: () => this.generateId(),
      now: () => this.now(), saveStudies: () => this.saveStudies(), saveStudyGroups: () => this.saveStudyGroups(),
    };
  }

  startNewStudy(): void {
    this.editingStudy = startNewStudy(this.editingDeps);
  }

  loadStudyForEditing(id: string): void {
    const study = this.studies.find((s) => s.id === id);
    if (study) this.editingStudy = { ...study };
  }

  updateEditingStudy(updates: Partial<WorkingStudy>): void {
    if (this.editingStudy) this.editingStudy = { ...this.editingStudy, ...updates };
  }

  clearEditingStudy(): void { this.editingStudy = null; }

  saveEditingStudy(): string | null {
    const result = saveEditingStudy(this.editingDeps);
    if (result.id) {
      this.studies = result.studies;
      this.studyGroups = result.studyGroups;
      this.editingStudy = result.editingStudy;
      this.saveStudies();
      this.saveStudyGroups();
    }
    return result.id;
  }

  // ── Study Group CRUD (delegated) ────────────────────────────

  private get groupDeps(): GroupStoreDeps {
    return {
      studyGroups: this.studyGroups,
      generateId: () => this.generateId(),
      now: () => this.now(),
      saveStudyGroups: () => this.saveStudyGroups(),
    };
  }

  addStudyGroup(name: string, description?: string, leaderCount: number = 1): string {
    const id = addStudyGroup(this.groupDeps, name, description, leaderCount);
    this.studyGroups = this.groupDeps.studyGroups;
    return id;
  }

  updateStudyGroup(id: string, updates: Partial<Omit<StudyGroup, 'id' | 'students'>>): void {
    updateStudyGroup(this.groupDeps, id, updates); this.studyGroups = this.groupDeps.studyGroups;
  }

  removeStudyGroup(id: string): void {
    // Inlined (not going through study-group-ops) because the deps-injection
    // path saves before `this.studyGroups` is updated, leaving the persisted
    // list out of sync — the group "comes back" on reload. Studies that
    // reference this group are intentionally NOT touched.
    this.studyGroups = this.studyGroups.filter((g) => g.id !== id);
    this.saveStudyGroups();
  }

  getStudyGroup(id: string): StudyGroup | undefined { return getStudyGroup(this.studyGroups, id); }

  setStudyGroupMemoryStart(groupId: string, location: BibleLocation | null): void {
    setStudyGroupMemoryStart(this.groupDeps, groupId, location); this.studyGroups = this.groupDeps.studyGroups;
  }

  setStudyGroupMemoryEnd(groupId: string, location: BibleLocation | null): void {
    setStudyGroupMemoryEnd(this.groupDeps, groupId, location); this.studyGroups = this.groupDeps.studyGroups;
  }

  // ── Members (delegated) ─────────────────────────────────────

  addStudentToGroup(groupId: string, student: StudentAddOptions): string | null {
    const id = addStudentToGroup(this.groupDeps, groupId, student); this.studyGroups = this.groupDeps.studyGroups; return id;
  }

  updateStudent(groupId: string, studentId: string, updates: Partial<Omit<import('@skjv/common').Student, 'id'>>): void {
    updateStudent(this.groupDeps, groupId, studentId, updates); this.studyGroups = this.groupDeps.studyGroups;
  }

  removeStudentFromGroup(groupId: string, studentId: string): void {
    removeStudentFromGroup(this.groupDeps, groupId, studentId); this.studyGroups = this.groupDeps.studyGroups;
  }

  addTeacherToGroup(groupId: string, name: string): string | null {
    const id = addTeacherToGroup(this.groupDeps, groupId, name); this.studyGroups = this.groupDeps.studyGroups; return id;
  }

  removeTeacherFromGroup(groupId: string, teacherId: string): void {
    removeTeacherFromGroup(this.groupDeps, groupId, teacherId); this.studyGroups = this.groupDeps.studyGroups;
  }

  createAssignmentsForGroup(groupId: string): StudentAssignment[] {
    return createAssignmentsForGroup(this.studyGroups, groupId);
  }

  // ── Export/Import (delegated) ───────────────────────────────

  exportStudyToJson(id: string): string | null { return exportStudyToJson(this.studies, this.studyGroups, id); }
  exportAllToJson(): string { return exportAllToJson(this.studies, this.studyGroups, this.now()); }

  private get importDeps(): StudyStoreDeps {
    return { studies: this.studies, studyGroups: this.studyGroups, generateId: () => this.generateId(), now: () => this.now(), saveStudies: () => this.saveStudies(), saveStudyGroups: () => this.saveStudyGroups() };
  }

  importFromJson(rawData: string): ImportResult { return importFromJson(rawData, this.importDeps); }
  importRawStudyWithGroup(data: Record<string, unknown>, groupName: string): { studiesCount: number; groupsCount: number } { return importRawStudyWithGroup(data, groupName, this.importDeps); }

  // ── Bulk import (idempotent, additive) ─────────────────────
  /**
   * Merge `incomingStudies` and `incomingGroups` into the in-memory state and
   * persist via the storage router. Studies are matched by id (callers should
   * use stable derived ids). Groups are added only when their id is missing —
   * existing groups are preserved untouched.
   */
  async bulkImport(incomingStudies: Study[], incomingGroups: StudyGroup[]): Promise<void> {
    const studyById = new Map(this.studies.map((s) => [s.id, s]));
    for (const s of incomingStudies) studyById.set(s.id, s);
    this.studies = Array.from(studyById.values());

    const groupById = new Map(this.studyGroups.map((g) => [g.id, g]));
    let groupsChanged = false;
    for (const g of incomingGroups) {
      if (!groupById.has(g.id)) {
        groupById.set(g.id, g);
        groupsChanged = true;
      }
    }
    if (groupsChanged) this.studyGroups = Array.from(groupById.values());

    await bulkSaveStudies(incomingStudies);
    if (groupsChanged) await bulkSaveStudyGroups(incomingGroups);
  }

  // ── Persistence ─────────────────────────────────────────────

  private saveStudies(): void { saveStudies(this.studies); }
  private saveStudyGroups(): void { saveStudyGroups(this.studyGroups); }
}
